import React, { useState, useEffect } from 'react'

import { Box, Tooltip, FormControl, InputLabel, Select, IconButton, MenuItem, ListItemText, ListItemIcon, Checkbox, InputAdornment, Input, Chip } from '@mui/material'
import { useTranslation } from "react-i18next"
import { useNavigate, useLocation } from 'react-router-dom'

import AppLayout from '../../components/Layouts/AppLayout'
import TableShared from '../../components/TableShared'
import { useStateContext } from '../../context/ContextProvider'
import { useAuth } from '../../hooks/auth'
import axios from '../../lib/axios'
import ClearIcon from "@mui/icons-material/Clear"
import Loading from '../../components/Loading'

import { handleDeleteSelectionObject, handleDeleteSelectionPrimitive, isValid, toTitleCase } from '../../helpers/helper'
import { useDebounce } from 'use-debounce'
import { useLocalStorage } from "@uidotdev/usehooks"
import { buildUrl } from '../../helpers/apiHelper'
import ServerTable from '../../components/ServerTable'

const defaultSort = {
    field: 'so_number',
    sort: 'desc'
}

const ProceededCollections = () => {

    const { t } = useTranslation()
    const { choosesite, config, pusher, currency } = useStateContext()
    const { user } = useAuth({ middleware: 'guest' })
    const [filters] = useLocalStorage('filters', null)

    const [isLoading, setIsLoading] = useState(false)
    const [purchaseOrders, setPurchaseOrders] = useState([])

    /* search options */
    const [searchStatuses, setSearchStatuses] = useState([])
    const [searchVendorAddresses, setSearchVendorAddresses] = useState([])
    const [searchReqDates, setSearchReqDates] = useState([])

    /* states */
    const [searchNo, setSearchNo] = useState(() => {
        if(filters) {
            return filters[0]?.customer_portal_proceeded_collections_no ?? '';
        }

        return '';
    })
    const [debouncedSearchNo] = useDebounce(searchNo, 500);

    const [selectedStatuses, setSelectedStatuses] = useState(() => {
        if(filters) {
            return filters[0]?.customer_portal_proceeded_collections_status ?? [];
        }

        return [];
    })
    const everyStatusSelected = searchStatuses.length > 0 && selectedStatuses.length === searchStatuses.length

    const [selectedVendorAddresses, setSelectedVendorAddresses] = useState(() => {
        if(filters) {
            return filters[0]?.customer_portal_proceeded_collections_location ?? [];
        }

        return [];
    })
    const everyVendorAddressSelected = searchVendorAddresses.length > 0 && selectedVendorAddresses.length === searchVendorAddresses.length

    const [selectedReqDates, setSelectedReqDates] = useState(() => {
        if(filters) {
            return filters[0]?.customer_portal_proceeded_collections_date ?? [];
        }

        return [];
    })
    const everyReqDateSelected = searchReqDates.length > 0 && selectedReqDates.length === searchReqDates.length

    const [openStatuses, setOpenStatuses] = useState(false)
    const [openVendorAddresses, setOpenVendorAddresses] = useState(false)
    const [openReqDates, setOpenReqDates] = useState(false)

    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [sort, setSort] = useState(defaultSort)

    const [model, setModel] = useState({
        po_number: true,
        requested_collection_date: true,
        po_status: true,
        purchase_address_name: true,
        customer_bol_no: true,
        broker_bol_no: true,
        pickup_trailer_no: true,
        total_qty_ordered: true,
        total_qty_received: true,
        total_ordered_load_value: true,
        actions: true
    })

    /* effects */

    useEffect(() => {
        if (isValid(choosesite)) {
            const collectionChannelCreate = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-created-site-${choosesite}`)
            const collectionChannelDelete = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)
            const collectionChannelUpdate = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${choosesite}`)

            // Bind collection channel pushes

            collectionChannelCreate.bind(`${localStorage.getItem('client_id')}-purchaseorder-created-event-site-${choosesite}`, data => {
                if (Number(data.vendor_id) == Number(user?.vendor_id)) {
                    /* handleEventCollection(data.id, 'created') */
                    getCollections()
                }
            })

            collectionChannelDelete.bind(`${localStorage.getItem('client_id')}-purchaseorder-deleted-event-site-${choosesite}`, data => {
                if (Number(data.vendor_id) == Number(user?.vendor_id)) {
                    /* handleEventCollection(data.id, 'deleted') */
                    getCollections()
                }
            })

            collectionChannelUpdate.bind(`${localStorage.getItem('client_id')}-purchaseorder-updated-event-site-${choosesite}`, data => {
                if (Number(data.vendor_id) == Number(user?.vendor_id)) {
                    /* handleEventCollection(data.id, 'updated') */
                    getCollections()
                }
            })
        }

        return (() => {
            if (isValid(choosesite)) {
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-created-site-${choosesite}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${choosesite}`)
            }
        })
    }, [choosesite])

    useEffect(() => {
        getTableView()
    }, [])

    useEffect(() => {
        if(isValid(choosesite)) {
            getCollections()
        }

        createFilterArray(searchNo, selectedStatuses, selectedVendorAddresses, selectedReqDates)
    }, [choosesite, pageSize, page, debouncedSearchNo, selectedStatuses, selectedVendorAddresses, selectedReqDates, sort])

    /* columns */

    const getColumns = () => {
        const columns = [];

        columns.push(
            {
                field: 'po_number',
                headerName: t('purchase_order_no'),
                flex: 0.75,
                minWidth: 150
            },
            {
                field: 'requested_collection_date',
                headerName: t('req_date'),
                flex: 0.625,
                minWidth: 125
            },
            {
                field: 'po_status_name',
                headerName: t('status'),
                flex: 1,
                minWidth: 200
            },
            {
                field: 'purchase_address_name',
                headerName: t('address'),
                flex: 1,
                minWidth: 200
            },
            {
                field: 'customer_bol_no',
                headerName: `${t('customer_bol')} #`,
                flex: 0.625,
                minWidth: 125
            },
            {
                field: 'broker_bol_no',
                headerName: `${t('broker_bol')} #`,
                flex: 0.625,
                minWidth: 125
            },
            {
                field: 'pickup_trailer_no',
                headerName: t('trailer_no'),
                flex: 0.625,
                minWidth: 125
            }
        );

        if(['arbor-trace', 'qa'].includes(localStorage.getItem('client_id'))) {
            columns.push(
                {
                    field: 'total_qty_received',
                    headerName: t('total_qty_x', {x: t('received') }),
                    flex: 0.7,
                    minWidth: 140
                }
            )
        } else {
            columns.push(
                {
                    field: 'total_qty_ordered',
                    headerName: t('total_qty_x', {x:t('ordered') }),
                    flex: 0.7,
                    minWidth: 140
                }
            )
        }

        columns.push(
            {
                field: 'total_ordered_load_value',
                headerName: t('total_load_value'),
                flex: 0.7,
                minWidth: 140,
                renderCell: (params) => <TotalLoadValue params={params} currency={currency} />
            },
            {
                field: 'actions',
                headerName: t('actions'),
                sortable: false,
                flex: 0.75,
                minWidth: 150,
                cellClassName: 'padding-0',
                renderCell: (params) => <TodaysPurchaseOrderAction params={params} setIsLoading={setIsLoading} config={config} />
            }
        )

        return columns;
    }

    /* functions */

    const getTableView = async () => {
        setIsLoading(true)
        await axios.get(`/api/get-table-view?view_name=customer_collections_table&user_id=${user?.id}`, config)
            .then(res => {
                const model = res.data
                if (Object.keys(model?.success?.data).length > 0) {
                    setModel(model.success.data)
                }
            })
            .catch(({ response }) => {
                console.error(response)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getCollections = async () => {
        setIsLoading(true)

        let joinedStatuses = null;
        let joinedAddresses = null;
        let joinedDates = null;

        try {
            joinedStatuses = selectedStatuses.map((status) => status?.id).join(',');
            joinedAddresses = selectedVendorAddresses.map((address) => address.id).join(',');
            joinedDates = selectedReqDates.join(',');
        } catch (e) {}

        let url = buildUrl(
            '/api/list-orders',
            {
                where: {
                    order_type: 'purchase',
                    customer_id: user?.customer_id,
                    po_number: searchNo,
                    address: joinedAddresses,
                    status_id: joinedStatuses,
                    r_dates: joinedDates,
                    include_broker: true,
                    page: page + 1,
                    page_size: pageSize
                }
            }
        );

        if(sort) {
            url = url.concat(`&sort=${sort?.field},${sort?.sort}`)
        }

        await axios
            .get(url, config)
            .then(res => {
                const data = res.data?.data;
                const count = res.data?.total;
                const dates = res?.data?.available_dates;
                const statuses = res?.data?.available_statuses;
                const addresses = res?.data?.available_vendor_addresses;
                setPurchaseOrders(data);
                setSearchStatuses(statuses);
                setSearchReqDates(dates);
                setSearchVendorAddresses(addresses);
                setTotal(count)
            })
            .catch(({ response }) => {
                console.error(response)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleChangeSelection = (event, type) => {
        const { target: { value } } = event
        let duplicateRemoved = []

        value?.forEach((val) => {
            switch (type) {
                case 'status':
                case 'vendorAddress':
                    if (duplicateRemoved.findIndex((o) => o.id === val.id) >= 0) {
                        duplicateRemoved = duplicateRemoved.filter((x) => x.id === val.id)
                    } else {
                        duplicateRemoved.push(val)
                    }
                    break
                case 'customerBOL':
                case 'brokerBOL':
                case 'reqDate':
                    if (duplicateRemoved.findIndex((o) => o === val) >= 0) {
                        duplicateRemoved = duplicateRemoved.filter((x) => x === val)
                    } else {
                        duplicateRemoved.push(val)
                    }
                    break
                default:
            }
        })

        if (value[value.length - 1] === 'all') {
            switch (type) {
                case 'status':
                    setSelectedStatuses(selectedStatuses.length === searchStatuses.length ? [] : searchStatuses)
                    break
                case 'vendorAddress':
                    setSelectedVendorAddresses(selectedVendorAddresses.length === searchVendorAddresses.length ? [] : searchVendorAddresses)
                    break
                case 'reqDate':
                    setSelectedReqDates(selectedReqDates.length === searchReqDates.length ? [] : searchReqDates)
                    break
                default:
                    return
            }
            return
        }

        switch (type) {
            case 'status':
                setSelectedStatuses(duplicateRemoved)
                break
            case 'vendorAddress':
                setSelectedVendorAddresses(duplicateRemoved)
                break
            case 'reqDate':
                setSelectedReqDates(duplicateRemoved)
                break
            default:
                return
        }
    }

    const createFilterArray = (no, status, location, date) => {
        if (localStorage.getItem('filters') === null) {
            let filter = [{}]
            localStorage.setItem('filters', JSON.stringify(filter))
        }

        let filters = JSON.parse(localStorage.getItem('filters'))

        filters[0].customer_portal_proceeded_collections_no = no
        filters[0].customer_portal_proceeded_collections_status = status
        filters[0].customer_portal_proceeded_collections_location = location
        filters[0].customer_portal_proceeded_collections_date = date

        localStorage.setItem('filters', JSON.stringify(filters))
    }

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>
                <div className='flex justify-between'>
                    <div className='p-5 w-full'>
                        <div className='pb-5 bg-white mb-2 rounded-md'>
                            <div className='flex justify-start gap-4 items-center p-5 border-b'>
                                <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('collections')}</p>
                            </div>
                            <div className='flex justify-between items-end lg:flex-row flex-col w-full pb-5 border-b'>
                                <div className='px-5 pt-5 w-full'>
                                    <div className='flex justify-between items-center search'>
                                        <input type="text" placeholder={t('search_by_purchase_no')} disabled={isLoading} value={searchNo} className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place' onChange={(e) => setSearchNo(e.target.value)} />
                                        <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                    </div>
                                </div>

                                <div className='px-5 pt-5 w-full'>
                                    <FormControl sx={{ width: 'inherit' }} focused={openStatuses}>
                                        <InputLabel id="select-statuses-label">{t('search_by_status')}</InputLabel>
                                        <Select
                                            multiple
                                            id="select-statuses"
                                            labelId='select-statuses-label'
                                            open={openStatuses}
                                            onOpen={() => setOpenStatuses(true)}
                                            onClose={() => setOpenStatuses(false)}
                                            disabled={isLoading}
                                            value={selectedStatuses}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((x) => (
                                                        <Chip
                                                            key={`s_option-${x.id}`}
                                                            label={toTitleCase(t(x?.key))}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onDelete={(e) => handleDeleteSelectionObject(e, x.id, selectedStatuses, setSelectedStatuses, setOpenStatuses)}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                            onChange={(e) => handleChangeSelection(e, 'status')}
                                            sx={{
                                                boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 'none !important' }, background: '#F5F5F5', borderRadius: '6px',
                                                ".MuiSelect-iconOutlined": { display: (selectedStatuses.length > 0) ? 'none !important' : '' }, "&.Mui-focused .MuiIconButton-root": { color: 'rgba(0,0,0,.42)' }
                                            }}
                                            endAdornment={selectedStatuses ? (<IconButton sx={{ visibility: (selectedStatuses.length > 0) ? "visible" : "hidden", padding: '0' }} onClick={() => setSelectedStatuses([])}><ClearIcon /></IconButton>) : false}
                                        >
                                            {
                                                searchStatuses?.length > 0 ?
                                                    <MenuItem value='all'>
                                                        <ListItemIcon>
                                                            <Checkbox checked={everyStatusSelected} indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < searchStatuses.length} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={t('select_all')} />
                                                    </MenuItem>
                                                    :
                                                    <MenuItem value='nooptions' disabled sx={{ textDecoration: 'italic' }}>{t('no_options')}</MenuItem>

                                            }

                                            {
                                                searchStatuses?.map((status) =>
                                                    <MenuItem value={status} key={`s${status.id}`}>
                                                        <Checkbox checked={selectedStatuses?.findIndex((i) => i.id == status.id) >= 0} />
                                                        <ListItemText primary={toTitleCase(t(status?.key))} />
                                                    </MenuItem>
                                                )
                                            }
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className='px-5 pt-5 w-full'>
                                    <FormControl sx={{ width: 'inherit' }} focused={openVendorAddresses}>
                                        <InputLabel id="select-vendor-addresses-label">{t('search_by_purchase_location')}</InputLabel>
                                        <Select
                                            multiple
                                            id="select-vendor-addresses"
                                            labelId='select-vendor-addresses-label'
                                            open={openVendorAddresses}
                                            onOpen={() => setOpenVendorAddresses(true)}
                                            onClose={() => setOpenVendorAddresses(false)}
                                            disabled={isLoading}
                                            value={selectedVendorAddresses}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((x) => (
                                                        <Chip
                                                            key={`va_option-${x.id}`}
                                                            label={x.name}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onDelete={(e) => handleDeleteSelectionObject(e, x.id, selectedVendorAddresses, setSelectedVendorAddresses, setOpenVendorAddresses)}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                            onChange={(e) => handleChangeSelection(e, 'vendorAddress')}
                                            sx={{
                                                boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 'none !important' }, background: '#F5F5F5', borderRadius: '6px',
                                                ".MuiSelect-iconOutlined": { display: (selectedVendorAddresses.length > 0) ? 'none !important' : '' }, "&.Mui-focused .MuiIconButton-root": { color: 'rgba(0,0,0,.42)' }
                                            }}
                                            endAdornment={selectedVendorAddresses ? (<IconButton sx={{ visibility: (selectedVendorAddresses.length > 0) ? "visible" : "hidden", padding: '0' }} onClick={() => setSelectedVendorAddresses([])}><ClearIcon /></IconButton>) : false}
                                        >

                                            {
                                                searchVendorAddresses?.length > 0 ?
                                                    <MenuItem value='all'>
                                                        <ListItemIcon>
                                                            <Checkbox checked={everyVendorAddressSelected} indeterminate={selectedVendorAddresses.length > 0 && selectedVendorAddresses.length < searchVendorAddresses.length} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={t('select_all')} />
                                                    </MenuItem>
                                                    :
                                                    <MenuItem value='nooptions' disabled sx={{ textDecoration: 'italic' }}>{t('no_options')}</MenuItem>
                                            }
                                            {
                                                searchVendorAddresses?.map((vendorAddress) =>
                                                    <MenuItem value={vendorAddress} key={`pa${vendorAddress.id}`}>
                                                        <Checkbox checked={selectedVendorAddresses?.findIndex((i) => i.id === vendorAddress.id) >= 0} />
                                                        <ListItemText primary={vendorAddress.name} />
                                                    </MenuItem>
                                                )
                                            }
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className='px-5 pt-5 w-full'>
                                    <FormControl sx={{ width: 'inherit' }} focused={openReqDates}>
                                        <InputLabel id="select-requested-collection-dates-label">{t('search_by_requested_collection_date')}</InputLabel>
                                        <Select
                                            multiple
                                            id="select-requested-collection-dates"
                                            labelId='select-requested-collection-dates-label'
                                            open={openReqDates}
                                            onOpen={() => setOpenReqDates(true)}
                                            onClose={() => setOpenReqDates(false)}
                                            disabled={isLoading}
                                            value={selectedReqDates}
                                            renderValue={(selected) => (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {selected.map((x) => (
                                                        <Chip
                                                            key={`rcd_option-${x}`}
                                                            label={x}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onDelete={(e) => handleDeleteSelectionPrimitive(e, x, selectedReqDates, setSelectedReqDates, setOpenReqDates)}
                                                        />
                                                    ))}
                                                </Box>
                                            )}
                                            onChange={(e) => handleChangeSelection(e, 'reqDate')}
                                            sx={{
                                                boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 'none !important' }, background: '#F5F5F5', borderRadius: '6px',
                                                ".MuiSelect-iconOutlined": { display: (selectedReqDates.length > 0) ? 'none !important' : '' }, "&.Mui-focused .MuiIconButton-root": { color: 'rgba(0,0,0,.42)' }
                                            }}
                                            endAdornment={selectedReqDates ? (<IconButton sx={{ visibility: (selectedReqDates.length > 0) ? "visible" : "hidden", padding: '0' }} onClick={() => setSelectedReqDates([])}><ClearIcon /></IconButton>) : false}
                                        >
                                            {
                                                searchReqDates?.length > 0 ?
                                                    <MenuItem value='all'>
                                                        <ListItemIcon>
                                                            <Checkbox checked={everyReqDateSelected} indeterminate={selectedReqDates.length > 0 && selectedReqDates.length < searchReqDates.length} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={t('select_all')} />
                                                    </MenuItem>
                                                    :
                                                    <MenuItem value='nooptions' disabled sx={{ textDecoration: 'italic' }}>{t('no_options')}</MenuItem>

                                            }
                                            {
                                                searchReqDates?.map((reqDate) =>
                                                    <MenuItem value={reqDate} key={reqDate}>
                                                        <Checkbox checked={selectedReqDates?.findIndex((i) => i === reqDate) >= 0} />
                                                        <ListItemText primary={reqDate} />
                                                    </MenuItem>
                                                )
                                            }
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>
                        </div>
                        <div className='pt-3'>
                            <ServerTable
                                columns={getColumns()}
                                items={purchaseOrders}
                                total={total}
                                setPage={setPage}
                                setPageSize={setPageSize}
                                page={page}
                                pageSize={pageSize}
                                columnVisibilityModel={model}
                                setModel={setModel}
                                view='customer_collections'
                                setSort={setSort}
                                defaultSort={defaultSort}
                            />
                        </div>

                    </div>
                </div>
            </AppLayout>
        </>
    )
}

export default ProceededCollections

const TodaysPurchaseOrderAction = (params) => {

    const { id, uploaded_bol, uploaded_driver_bol, broker_transaction } = params.params.row

    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const getPodUrl = (
        stream = 'view',
        party = 'customer'
    ) => {
        if(uploaded_driver_bol)
            return uploaded_driver_bol?.document_url

        let urlStem = `purchase-orders/${id}`

        if(broker_transaction && broker_transaction?.uploaded_bol)
            return broker_transaction.uploaded_bol.document_url

        if(uploaded_bol)
            return uploaded_bol.document_url

        const urlRoot = `${process.env.REACT_APP_BACKEND_URL}/api`
        const urlLeaves = `proof-of-delivery?stream=${stream}&party=${party}&CLIENT_ID=${localStorage.getItem('client_id')}&CLIENT_TIMEZONE=${encodeURIComponent(localStorage.getItem('client_timezone'))}`

        return `${urlRoot}/${urlStem}/${urlLeaves}`
    }

    return (
        <>
            <div className='flex justify-between'>
                <Tooltip disableInteractive title={t('open')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <button onClick={() => navigate(`/purchase-order/${id}`, { state: { prevPathname: location.pathname } })} ><span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-eye"></i></span></button>
                    </div>
                </Tooltip>

                <Tooltip disableInteractive title="BOL/POD" placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <a href={getPodUrl()} target="_blank"><span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-print"></i></span></a>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}

const TotalLoadValue = ({params, currency}) => {

    const { row } = params;
    const { total_load_value } = row;

    const loadValue = total_load_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formattedLoadValue = currency ? `${currency.prefix}${loadValue}${currency.suffix}` : `${loadValue}`

    return (
        <>{formattedLoadValue}</>
    )
}
